import React, { useEffect, useState } from 'react';
import { doc, getDoc, setDoc, collection, getDocs, addDoc, updateDoc, deleteDoc, orderBy, query, limit, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../services/firebase';
import { AppSettings, DEFAULT_SETTINGS, CheckinReason, CadastroCheck } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { importarClientesAppOnly } from '../../utils/importarAppOnly';
import { importarPotencialEstrelas } from '../../utils/importarPotencialEstrelas';
import { importarVisitasHistoricas } from '../../utils/importarVisitasHistoricas';

// Progresso AO VIVO das rotinas longas: as functions gravam { total, feitos } num doc em `settings/`
// (helper _progresso) e aqui só escutamos enquanto a rotina roda. `feitos` vem como NÚMERO no helper
// genérico e como ARRAY no import de equipes (lá o doc também serve p/ retomar) — tratamos os dois.
function useProgresso(docId: string, running: boolean) {
  const [p, setP] = useState<{ feitos: number; total: number; eta: string } | null>(null);
  const ini = React.useRef<{ t: number; feitos: number } | null>(null);
  useEffect(() => {
    if (!running) { setP(null); ini.current = null; return; }
    const unsub = onSnapshot(doc(db, 'settings', docId), (snap) => {
      const d: any = snap.data();
      if (!d || !d.total) return;
      const feitos = Array.isArray(d.feitos) ? d.feitos.length : (Number(d.feitos) || 0);
      if (!ini.current) ini.current = { t: Date.now(), feitos };
      // Estimativa pela velocidade observada NESTA rodada (o `feitos` pode já vir de cliques anteriores).
      const dt = (Date.now() - ini.current.t) / 1000;
      const df = feitos - ini.current.feitos;
      let eta = '';
      if (df > 0 && dt > 5) {
        const seg = Math.round((d.total - feitos) / (df / dt));
        eta = seg > 90 ? `~${Math.ceil(seg / 60)} min restantes` : `~${seg}s restantes`;
      }
      setP({ feitos, total: d.total, eta });
    }, () => { /* sem progresso: o botão só mostra "carregando" */ });
    return () => unsub();
  }, [running, docId]);
  return p;
}

const pctDe = (p: { feitos: number; total: number } | null) => (p && p.total ? Math.round((p.feitos / p.total) * 100) : null);

function BarraProgresso({ p }: { p: { feitos: number; total: number; eta: string } | null }) {
  const pct = pctDe(p);
  if (pct == null || !p) return null;
  return (
    <span style={{ ...styles.hint, marginLeft: 12 }}>
      <span style={{ display: 'inline-block', width: 140, height: 8, background: '#f0f0f0', borderRadius: 4, overflow: 'hidden', verticalAlign: 'middle', marginRight: 8 }}>
        <span style={{ display: 'block', width: `${pct}%`, height: '100%', background: '#f5821f' }} />
      </span>
      {p.feitos}/{p.total}{p.eta ? ` · ${p.eta}` : ''}
    </span>
  );
}

export default function SettingsPage() {
  // Papel LITERAL: só `admin` vê a tela completa. Os demais (diretor/pricing/GN/gerente/coordenador/
  // supervisor/vendedor) veem apenas o "Sincronizar Clientes". Não usar `isAdmin` do contexto — ele é
  // `isFullAccess`, que inclui diretor/pricing/GN.
  const { user } = useAuth();
  const isAdminRole = user?.role === 'admin';
  const [settings, setSettings] = useState<AppSettings>(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');
  const [reasons, setReasons] = useState<CheckinReason[]>([]);
  const [newReason, setNewReason] = useState('');
  const [editingReason, setEditingReason] = useState<CheckinReason | null>(null);
  const [lastRun, setLastRun] = useState<CadastroCheck | null>(null);
  const [running, setRunning] = useState(false);
  const [runMsg, setRunMsg] = useState('');
  const [runningCompra, setRunningCompra] = useState(false);
  const [compraMsg, setCompraMsg] = useState('');
  const [runningGeo, setRunningGeo] = useState(false);
  const [geoMsg, setGeoMsg] = useState('');
  const [runningHier, setRunningHier] = useState(false);
  const [hierMsg, setHierMsg] = useState('');
  const [runningQuery, setRunningQuery] = useState(false);
  const [queryMsg, setQueryMsg] = useState('');
  const [importCodvend, setImportCodvend] = useState('');
  const [runningImport, setRunningImport] = useState(false);
  const [importMsg, setImportMsg] = useState('');
  const [runningEstrelas, setRunningEstrelas] = useState(false);
  const [estrelasMsg, setEstrelasMsg] = useState('');
  const [estrelasErro, setEstrelasErro] = useState(false);
  const [runningVisitas, setRunningVisitas] = useState(false);
  const [visitasMsg, setVisitasMsg] = useState('');
  const [visitasErro, setVisitasErro] = useState(false);
  const [runningEstrelaPadrao, setRunningEstrelaPadrao] = useState(false);
  const [estrelaPadraoMsg, setEstrelaPadraoMsg] = useState('');
  const [estrelaPadraoErro, setEstrelaPadraoErro] = useState(false);
  // Progresso ao vivo de cada rotina longa (cada uma grava no seu doc em `settings/`).
  const progGeo = useProgresso('progresso_geocode', runningGeo);
  const progSync = useProgresso('progresso_sincronizarClientes', runningQuery);

  const loadReasons = async () => {
    const snap = await getDocs(query(collection(db, 'checkinReasons'), orderBy('name')));
    setReasons(snap.docs.map(d => ({ id: d.id, ...d.data() })) as CheckinReason[]);
  };

  const loadLastRun = async () => {
    const snap = await getDocs(query(collection(db, 'cadastroChecks'), orderBy('runAt', 'desc'), limit(1)));
    if (!snap.empty) {
      const d = snap.docs[0];
      const raw = d.data();
      setLastRun({ id: d.id, ...raw, runAt: raw.runAt?.toDate?.() ?? new Date() } as CadastroCheck);
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const snap = await getDoc(doc(db, 'settings', 'app'));
        if (snap.exists()) {
          setSettings({ ...DEFAULT_SETTINGS, ...snap.data() } as AppSettings);
        }
        // SÓ admin: `cadastroChecks` exige acesso total nas regras (e `checkinReasons` só interessa
        // à tela completa). Sem esta guarda, gestor/vendedor abriam Configurações e tomavam
        // "Missing or insufficient permissions" — a tela reduzida não usa nenhum desses dados.
        if (isAdminRole) {
          await loadReasons();
          await loadLastRun();
        }
      } catch (e) {
        console.warn('Configurações: falha ao carregar', e); // nunca deixa preso em "Carregando..."
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdminRole]);

  const handleRunNow = async () => {
    setRunning(true);
    setRunMsg('');
    try {
      const fn = httpsCallable(functions, 'verificarCadastrosAgora');
      const res: any = await fn({});
      const r = res?.data;
      setRunMsg(`Verificação concluída: ${r?.pendingClients ?? 0} de ${r?.totalClients ?? 0} clientes com pendência.`);
      await loadLastRun();
    } catch (e: any) {
      setRunMsg(e?.message || 'Falha ao executar a verificação.');
    } finally {
      setRunning(false);
    }
  };

  const handleAtualizarCompra = async () => {
    setRunningCompra(true);
    setCompraMsg('');
    try {
      const fn = httpsCallable(functions, 'atualizarUltimaCompraAgora', { timeout: 540000 });
      const res: any = await fn({});
      setCompraMsg(`Concluído: ${res?.data?.atualizados ?? 0} cliente(s) com a última compra atualizada.`);
    } catch (e: any) {
      setCompraMsg(e?.message || 'Falha ao atualizar a última compra.');
    } finally {
      setRunningCompra(false);
    }
  };

  const handleGeocodificar = async () => {
    setRunningGeo(true);
    setGeoMsg('');
    try {
      const fn = httpsCallable(functions, 'geocodificarBaseAgora', { timeout: 540000 });
      const res: any = await fn({ max: 5000 }); // servidor limita: 5000 com Google, 150 no Nominatim
      const d = res?.data || {};
      setGeoMsg(
        `Concluído: ${d.processados ?? 0} processado(s), ${d.melhorados ?? 0} preciso(s), ${d.corrigidos ?? 0} corrigido(s)` +
        (d.google ? ' [Google].' : ' [Nominatim]. Clique de novo para continuar a base.') +
        (d.googleErro ? ` ⚠️ Google recusou ${d.googleErro} chamada(s) — verifique a chave/billing/quota.` : '')
      );
    } catch (e: any) {
      setGeoMsg(e?.message || 'Falha ao geocodificar.');
    } finally {
      setRunningGeo(false);
    }
  };


  const handleSincronizarHierarquia = async () => {
    setRunningHier(true);
    setHierMsg('');
    try {
      const fn = httpsCallable(functions, 'sincronizarHierarquiaAgora', { timeout: 540000 });
      const res: any = await fn({});
      setHierMsg(`Hierarquia sincronizada em ${res?.data?.atualizados ?? 0} usuário(s) (pricing/gerentes/coordenadores/supervisores já enxergam os vendedores deles).`);
    } catch (e: any) {
      setHierMsg(e?.message || 'Falha ao sincronizar a hierarquia.');
    } finally {
      setRunningHier(false);
    }
  };

  // IMPORTAÇÃO de clientes APP-ONLY (planilha de CNPJs FORA do Sankhya) → vinculados a um vendedor
  // pelo CODVEND. Marcados 'nao_cadastrado' (não sobem pro ERP, não são apagados pelo sync). Dedup por CNPJ.
  const handleImportarAppOnly = async (file: File) => {
    setRunningImport(true);
    setImportMsg('');
    try {
      const r = await importarClientesAppOnly(file, importCodvend);
      setImportMsg(
        `✅ ${r.criados} cliente(s) criado(s) para ${r.vendedor}` +
        (r.pulados ? `, ${r.pulados} pulado(s) (CNPJ já existia)` : '') +
        (r.semCnpj ? `, ${r.semCnpj} sem CNPJ` : '') +
        (r.semNome ? `, ${r.semNome} sem Razão Social` : '') +
        ` de ${r.totalPlanilha} linha(s). Marcados "Não cadastrado no Sankhya".`
      );
    } catch (e: any) {
      setImportMsg(e?.message || 'Falha ao importar.');
    } finally {
      setRunningImport(false);
    }
  };

  // IMPORTAÇÃO de ESTRELAS/Potencial (aba "Potencial_Estrelas" da planilha de roteirização) → casa por
  // CODPARC e grava estrelas (1–3) + Potencial de Venda/Compra SÓ nas células preenchidas (não zera manual).
  const handleImportarEstrelas = async (file: File) => {
    setRunningEstrelas(true);
    setEstrelasMsg('');
    setEstrelasErro(false);
    try {
      const r = await importarPotencialEstrelas(file);
      if (r.atualizados > 0) {
        setEstrelasMsg(
          `✅ ${r.atualizados} cadastro(s) atualizado(s) (${r.casados} CODPARC casado(s), ${r.enviadas} enviada(s))` +
          (r.semMatch ? `, ${r.semMatch} sem cliente na base` : '') +
          (r.semCodparc ? `, ${r.semCodparc} sem CODPARC` : '') +
          ` de ${r.total} linha(s).`
        );
      } else {
        // Nada gravado: distingue "planilha sem valores" de "CODPARC não existem na base".
        setEstrelasErro(true);
        setEstrelasMsg(
          r.enviadas === 0 && r.total > 0
            ? `⚠️ ${r.total} linha(s) lida(s), mas nenhuma com estrelas/potencial preenchido — confira as colunas da aba "Potencial_Estrelas".`
            : `⚠️ Nenhum cadastro atualizado` +
              (r.semMatch ? ` — ${r.semMatch} CODPARC sem cliente na base` : '') +
              (r.semCodparc ? `, ${r.semCodparc} sem CODPARC` : '') +
              ` de ${r.total} linha(s).`
        );
      }
    } catch (e: any) {
      setEstrelasErro(true);
      setEstrelasMsg('❌ ' + (e?.message || 'Falha ao importar.'));
    } finally {
      setRunningEstrelas(false);
    }
  };

  // IMPORTAÇÃO de VISITAS HISTÓRICAS (aba "Visitas" — histórico "Tebaldi" anterior ao app) → casa por
  // CODPARC e grava clients.lastCheckinAt só se a data for MAIS RECENTE que a atual (não apaga check-in real).
  const handleImportarVisitas = async (file: File) => {
    setRunningVisitas(true);
    setVisitasMsg('');
    setVisitasErro(false);
    try {
      const r = await importarVisitasHistoricas(file);
      // casados>0 já é sucesso: se tudo foi "preservado" (check-in real mais novo), a base só está fresca.
      if (r.atualizados > 0 || r.casados > 0) {
        setVisitasMsg(
          `✅ ${r.atualizados} cadastro(s) com última visita semeada` +
          ` (${r.casados} CODPARC casado(s)` +
          (r.preservados ? `, ${r.preservados} preservado(s) — check-in real mais novo` : '') + ')' +
          (r.semMatch ? `, ${r.semMatch} sem cliente na base` : '') +
          (r.semCodparc ? `, ${r.semCodparc} sem CODPARC` : '') +
          (r.semData ? `, ${r.semData} sem data` : '') +
          ` — ${r.codparcs} CODPARC de ${r.total} linha(s).`
        );
      } else {
        setVisitasErro(true);
        setVisitasMsg(
          r.codparcs === 0 && r.total > 0
            ? `⚠️ ${r.total} linha(s) lida(s), mas nenhuma com CODPARC/data válidos — confira a aba "Visitas".`
            : `⚠️ Nenhum CODPARC casou com a base — rode "Sincronizar Clientes" antes de importar as visitas` +
              (r.semMatch ? ` (${r.semMatch} sem cliente)` : '') +
              (r.semCodparc ? `, ${r.semCodparc} sem CODPARC` : '') +
              ` de ${r.total} linha(s).`
        );
      }
    } catch (e: any) {
      setVisitasErro(true);
      setVisitasMsg('❌ ' + (e?.message || 'Falha ao importar.'));
    } finally {
      setRunningVisitas(false);
    }
  };

  // Define 1★ padrão em TODOS os clientes sem estrela (0/ausente), preservando 1/2/3. Escrita grande.
  const handleEstrelaPadrao = async () => {
    if (!window.confirm('Definir 1★ como padrão em TODOS os clientes que estão com 0/sem estrela?\n\nNÃO altera quem já tem 1, 2 ou 3★. Ativa praticamente toda a base na cadência da roteirização (90 dias p/ 1★) — afeta todos os vendedores. É uma escrita grande (~15 mil clientes). Confirmar?')) return;
    setRunningEstrelaPadrao(true);
    setEstrelaPadraoMsg('');
    setEstrelaPadraoErro(false);
    try {
      const fn = httpsCallable(functions, 'definirEstrelaPadrao', { timeout: 300000 });
      const res: any = await fn({});
      const d = res?.data || {};
      setEstrelaPadraoMsg(
        `✅ ${d.atualizados} cliente(s) receberam 1★ · ${d.jaTinham} já tinham estrela · ${d.total} no total.` +
        (d.indicePublicado === false ? ' ⚠️ o índice do mapa não atualizou — rode "Sincronizar Clientes" para refletir no app/mapa.' : '')
      );
    } catch (e: any) {
      setEstrelaPadraoErro(true);
      setEstrelaPadraoMsg('❌ ' + (e?.message || 'Falha ao definir estrela padrão.'));
    } finally {
      setRunningEstrelaPadrao(false);
    }
  };

  // CONSULTA OFICIAL EM TEMPO REAL — roda a própria query no Sankhya (DbExplorerSP) e importa tudo.
  // Nunca desativa: quem sobrar sai como contagem/amostra, e a desativação continua na limpeza.
  //
  // A tela NÃO fica presa esperando: a rodada leva minutos, e antes o `await` da callable segurava o
  // painel o tempo todo. Agora dispara e solta — a barra vem de `progresso_sincronizarClientes` (que
  // já existia) e o desfecho de `resultado_sincronizarClientes`, gravado pela function no fim. A
  // Cloud Function roda até o fim mesmo se o navegador desconectar, então nada se perde ao sair daqui.
  const mensagemDaRodada = (d: any) => (
    `Consulta oficial AGORA: ${d.universo ?? 0} cliente(s)` +
    (d.completo ? '' : ' ⚠️ PAGINAÇÃO INCOMPLETA (estourou o tempo) — clique de novo') + '. ' +
    `${d.criados ?? 0} criado(s), ${d.atualizados ?? 0} atualizado(s)/religado(s)` +
    (d.inalterados ? `, ${d.inalterados} já igual(is) ao ERP (não regravado)` : '') + '.' +
    (d.coordSemeadas ? ` 📍 ${d.coordSemeadas} coordenada(s) do ERP plantada(s) no mapa (revalidadas em 2º plano).` : '') +
    (d.cnpjDuplicados ? ` ℹ️ ${d.cnpjDuplicados} c/ CNPJ repetido no Sankhya (doc próprio; lista no console).` : '') +
    (d.semDataParseavel ? ` ⚠️ ${d.semDataParseavel} data(s) de última venda em formato inesperado — preservado o que já estava.` : '') +
    (d.freioDeEmergencia
      ? ` 🛑 ${d.sobrando} cliente(s) ativos fora da query — MAIS DE 20% da base, então NADA foi desativado (freio de emergência): confira se a query/ERP está normal e rode de novo. Amostra no console.`
      : d.desativados
        ? ` 🧹 ${d.desativados} desativado(s) — saíram da consulta oficial (reversível; amostra no console).`
        : d.sobrando === 0
          ? ' ✅ Espelho exato da query — nada a desativar.'
          : '') +
    (d.apagados ? ` 🗑️ ${d.apagados} apagado(s) por carência (estavam desativados há mais dias que o limite).` : '') +
    (d.gestoresEscopo ? ` 👥 Escopo de ${d.gestoresEscopo} gestor(es) recalculado.` : '') +
    (d.escopoErro ? ' ⚠️ Não deu para recalcular o escopo dos gestores nesta rodada.' : '')
  );

  const handleImportarDaQuery = async () => {
    setRunningQuery(true);
    setQueryMsg('');
    const resRef = doc(db, 'settings', 'resultado_sincronizarClientes');

    let unsub: (() => void) | null = null;
    let fechado = false;
    // Três caminhos podem terminar a rodada (retorno da callable, doc de resultado, recusa imediata).
    // O primeiro que chegar manda; sem esta trava, um resultado tardio sobrescreveria a mensagem certa.
    const encerrar = (msg: string) => {
      if (fechado) return;
      fechado = true;
      if (unsub) { unsub(); unsub = null; }
      setRunningQuery(false);
      setQueryMsg(msg);
    };

    // Carimbo do resultado ANTERIOR: a rodada terminou quando ele mudar. Comparar carimbos entre si
    // evita depender do relógio do navegador contra o do servidor.
    // ⚠️ Se a leitura falhar, o baseline fica DESCONHECIDO — e aí o primeiro snapshot NÃO pode contar
    // como desfecho: o `onSnapshot` entrega de cara o doc que já está lá, da rodada passada, e a tela
    // anunciaria "concluída" no instante do clique com os números de ontem. Nesse caso adotamos o
    // primeiro snapshot COMO baseline e só aceitamos a mudança seguinte.
    let anterior: number | null = null;
    try { anterior = (await getDoc(resRef)).data()?.terminadoEm?.toMillis?.() ?? 0; }
    catch { anterior = null; }

    unsub = onSnapshot(resRef, (snap) => {
      const d: any = snap.data();
      const carimbo = d?.terminadoEm?.toMillis?.();
      if (carimbo == null) return;
      if (anterior == null) { anterior = carimbo; return; } // baseline desconhecido: este vira a régua
      if (carimbo === anterior) return;                     // ainda é o resultado da rodada passada
      encerrar(d.ok === false ? '❌ ' + (d.erro || 'Falha ao importar da consulta oficial.') : mensagemDaRodada(d));
    });

    // Dispara e SOLTA. O retorno da callable é o caminho PRINCIPAL de conclusão — chega direto, com o
    // payload completo, e não depende de o doc de resultado ter sido gravado (essa gravação é
    // best-effort e engole a própria falha, então tratá-la como única saída deixaria o botão girando
    // para sempre). O `onSnapshot` acima é a rede de segurança para quando a conexão cair no meio.
    httpsCallable(functions, 'importarDaConsultaOficial', { timeout: 540000 })({})
      .then((res: any) => {
        const d = res?.data || {};
        // As amostras carregam CNPJ/razão social: só vêm no retorno da callable, nunca no doc.
        if (d.amostraCnpjDuplicados?.length) console.log('CNPJ repetido entre CODPARCs:', d.amostraCnpjDuplicados);
        if (d.amostraSobrando?.length) console.log('SOBRANDO no app (ativos fora da consulta oficial):', d.amostraSobrando);
        encerrar(mensagemDaRodada(d));
      })
      .catch((e: any) => {
        const msg = e?.message || 'Falha ao importar da consulta oficial.';
        // ⚠️ Só encerra se for RECUSA (o servidor nem começou). Uma queda de transporte no meio de uma
        // rodada de minutos rejeita igual, mas ali o servidor SEGUE e termina bem — encerrar aqui daria
        // "Erro" numa sincronização que deu certo e mataria o ouvinte que traria a resposta verdadeira.
        const recusa = /already-exists|permission|unauthenticated|invalid-argument|failed-precondition|DbExplorer|executeQuery|não autorizado|not authorized/i.test(
          String(e?.code || '') + ' ' + msg
        );
        if (!recusa) { console.warn('Conexão caiu durante a sincronização; aguardando o resultado pelo doc:', msg); return; }
        encerrar(
          /DbExplorer|executeQuery|não autorizado|not authorized|permission/i.test(msg)
            ? `O gateway recusou o DbExplorerSP.executeQuery (${msg}). Sem essa permissão na credencial OAuth não dá para sincronizar — peça ao admin do Sankhya para liberá-la.`
            : msg
        );
      });
  };

  const addReason = async () => {
    const name = newReason.trim();
    if (!name) return;
    await addDoc(collection(db, 'checkinReasons'), { name, active: true, createdAt: new Date() });
    setNewReason('');
    loadReasons();
  };

  const saveReasonEdit = async () => {
    if (!editingReason || !editingReason.name.trim()) return;
    await updateDoc(doc(db, 'checkinReasons', editingReason.id), { name: editingReason.name.trim() });
    setEditingReason(null);
    loadReasons();
  };

  const toggleReason = async (r: CheckinReason) => {
    await updateDoc(doc(db, 'checkinReasons', r.id), { active: !r.active });
    loadReasons();
  };

  const deleteReason = async (r: CheckinReason) => {
    if (!window.confirm(`Excluir o motivo "${r.name}"?`)) return;
    await deleteDoc(doc(db, 'checkinReasons', r.id));
    loadReasons();
  };

  const set = <K extends keyof AppSettings>(key: K, value: AppSettings[K]) =>
    setSettings(prev => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    setSaving(true);
    setSavedMsg('');
    try {
      await setDoc(doc(db, 'settings', 'app'), settings, { merge: true });
      setSavedMsg('Configurações salvas! As mudanças valem para os próximos check-ins.');
    } catch {
      setSavedMsg('Erro ao salvar. Tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p>Carregando...</p>;

  // NÃO-ADMIN (diretor, pricing, gerente nacional, gerente, coordenador, supervisor, vendedor):
  // a tela mostra APENAS o "Sincronizar Clientes" manual. As regras do app, motivos de check-in,
  // rotinas e exportações continuam exclusivas do papel `admin`.
  // (Está DEPOIS de todos os hooks de propósito — hook após early-return derruba o app; ver a lição
  // do crash-loop no PROJECT_CONTEXT.)
  if (!isAdminRole) {
    return (
      <div style={{ maxWidth: 680 }}>
        <h1 style={styles.title}>Configurações</h1>
        <p style={styles.subtitle}>Atualize a base de clientes com o Sankhya.</p>
        <div style={styles.card}>
          <div style={styles.subField}>
            <button style={styles.runBtn} onClick={handleImportarDaQuery} disabled={runningQuery}>
              {runningQuery ? `Sincronizando Clientes...${pctDe(progSync) != null ? ` ${pctDe(progSync)}%` : ''}` : '🔄 Sincronizar Clientes'}
            </button>
            <BarraProgresso p={progSync} />
            {/* Sem o texto explicativo aqui de propósito: na visão reduzida (não-admin) só o botão e
                o resultado interessam. A explicação segue na tela completa do admin. */}
            {queryMsg && <span style={{ ...styles.hint, color: '#7c3aed', marginLeft: 12 }}>{queryMsg}</span>}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 680 }}>
      <h1 style={styles.title}>Configurações do App</h1>
      <p style={styles.subtitle}>Defina as regras que os vendedores seguem no aplicativo.</p>

      {savedMsg && <div style={styles.saved}>{savedMsg}</div>}

      <div style={styles.card}>
        <Toggle
          label="Limitar distância do check-in"
          desc="O check-in só é permitido se o vendedor estiver próximo do cliente (clientes precisam ter GPS cadastrado)."
          value={settings.checkinRadiusEnabled}
          onChange={v => set('checkinRadiusEnabled', v)}
        />
        {settings.checkinRadiusEnabled && (
          <div style={styles.subField}>
            <label style={styles.label}>Distância máxima (metros)</label>
            <input
              type="number"
              style={styles.numberInput}
              value={settings.checkinRadiusMeters}
              min={10}
              onChange={e => set('checkinRadiusMeters', Number(e.target.value))}
            />
            <span style={styles.hint}>Ex: 200 metros. Acima disso, o check-in é bloqueado.</span>
          </div>
        )}
      </div>

      <div style={styles.card}>
        <Toggle
          label="Vendedor pode cadastrar clientes"
          desc="Se desativado, apenas o gestor cadastra clientes pelo painel."
          value={settings.allowClientRegistrationBySeller}
          onChange={v => set('allowClientRegistrationBySeller', v)}
        />
      </div>

      <div style={styles.card}>
        <Toggle
          label="Verificação automática de cadastro"
          desc="Uma rotina diária analisa os clientes e sinaliza pendências (campos faltando, sem GPS, sem vendedor, sem visita há muito tempo)."
          value={settings.cadastroCheckEnabled}
          onChange={v => set('cadastroCheckEnabled', v)}
        />
        {settings.cadastroCheckEnabled && (
          <>
            <div style={styles.subField}>
              <label style={styles.label}>Horário da verificação (hora do dia)</label>
              <select
                style={styles.numberInput}
                value={settings.cadastroCheckHour}
                onChange={e => set('cadastroCheckHour', Number(e.target.value))}
              >
                {Array.from({ length: 24 }, (_, h) => (
                  <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>
                ))}
              </select>
              <span style={styles.hint}>A rotina roda todos os dias nesse horário (fuso de Brasília).</span>
            </div>
            <div style={styles.subField}>
              <label style={styles.label}>Dias sem visita para "cliente esquecido"</label>
              <input
                type="number"
                style={styles.numberInput}
                value={settings.cadastroForgottenDays}
                min={1}
                onChange={e => set('cadastroForgottenDays', Number(e.target.value))}
              />
              <span style={styles.hint}>Cliente sem nenhum check-in há mais que esse período é sinalizado.</span>
            </div>
          </>
        )}
        <div style={styles.subField}>
          <button style={styles.runBtn} onClick={handleRunNow} disabled={running}>
            {running ? 'Verificando...' : '▶ Verificar agora'}
          </button>
          {runMsg && <span style={{ ...styles.hint, color: '#16a34a', marginLeft: 12 }}>{runMsg}</span>}
          {lastRun && (
            <div style={styles.hint}>
              Última verificação: {lastRun.runAt.toLocaleString('pt-BR')} ({lastRun.triggeredBy === 'manual' ? 'manual' : 'automática'}) — {lastRun.pendingClients} de {lastRun.totalClients} com pendência.
            </div>
          )}
        </div>
      </div>

      <div style={styles.card}>
        <Toggle
          label="Geolocalização"
          desc="Registra a localização do vendedor em segundo plano no horário definido (seg a sex, dentro da janela abaixo). Fora da janela e no fim de semana não há gravação. Requer o app instalado e a permissão de localização o tempo todo."
          value={settings.trackingExpedienteEnabled}
          onChange={v => set('trackingExpedienteEnabled', v)}
        />
        {settings.trackingExpedienteEnabled && (
          <>
            <div style={styles.subField}>
              <label style={styles.label}>Início da janela</label>
              <input
                type="time"
                style={styles.numberInput}
                value={settings.trackingStartHour}
                onChange={e => set('trackingStartHour', e.target.value)}
              />
            </div>
            <div style={styles.subField}>
              <label style={styles.label}>Fim da janela</label>
              <input
                type="time"
                style={styles.numberInput}
                value={settings.trackingEndHour}
                onChange={e => set('trackingEndHour', e.target.value)}
              />
              <span style={styles.hint}>A janela usa o horário do aparelho do vendedor. Uma navegação até cliente iniciada antes do fim continua até o check-out.</span>
            </div>
            <div style={styles.subField}>
              <label style={styles.label}>Dias</label>
              <span style={styles.hint}>Segunda a sexta (fixo). Sábado e domingo não rastreiam.</span>
            </div>
          </>
        )}
      </div>

      <div style={styles.card}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: '#1a1a2e', margin: '0 0 4px' }}>Rotinas automáticas</h2>
        <p style={{ fontSize: 13, color: '#888', margin: '0 0 12px' }}>Rodam todo dia no horário escolhido (fuso de Brasília). Use os botões "▶ Atualizar agora" / "▶ Geocodificar agora" para rodar na hora.</p>

        <Toggle
          label="Atualizar 'Dias sem comprar' automaticamente"
          desc="Busca a última compra de cada cliente no Sankhya todo dia."
          value={settings.ultimaCompraAutoEnabled}
          onChange={v => set('ultimaCompraAutoEnabled', v)}
        />
        {settings.ultimaCompraAutoEnabled && (
          <div style={styles.subField}>
            <label style={styles.label}>Horário</label>
            <select style={styles.numberInput} value={settings.ultimaCompraHour} onChange={e => set('ultimaCompraHour', Number(e.target.value))}>
              {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>)}
            </select>
          </div>
        )}
        <div style={styles.subField}>
          <button style={styles.runBtn} onClick={handleAtualizarCompra} disabled={runningCompra}>
            {runningCompra ? 'Atualizando...' : '▶ Atualizar agora'}
          </button>
          {compraMsg && <span style={{ ...styles.hint, color: '#16a34a', marginLeft: 12 }}>{compraMsg}</span>}
        </div>

        <div style={{ borderTop: '1px solid #f0f0f0', marginTop: 16, paddingTop: 16 }}>
          <Toggle
            label="Geocodificar a base automaticamente"
            desc="Localiza/melhora as coordenadas dos clientes pendentes (aos poucos, por dia)."
            value={settings.geocodeAutoEnabled}
            onChange={v => set('geocodeAutoEnabled', v)}
          />
          {settings.geocodeAutoEnabled && (
            <div style={styles.subField}>
              <label style={styles.label}>Horário</label>
              <select style={styles.numberInput} value={settings.geocodeHour} onChange={e => set('geocodeHour', Number(e.target.value))}>
                {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>)}
              </select>
              <span style={styles.hint}>Processa até ~80 clientes por dia (limite do mapa gratuito); a base grande converge em alguns dias.</span>
            </div>
          )}
          <div style={styles.subField}>
            <button style={styles.runBtn} onClick={handleGeocodificar} disabled={runningGeo}>
              {runningGeo ? `Geocodificando...${pctDe(progGeo) != null ? ` ${pctDe(progGeo)}%` : ''}` : '▶ Geocodificar agora'}
            </button>
            <BarraProgresso p={progGeo} />
            {geoMsg && <span style={{ ...styles.hint, color: '#16a34a', marginLeft: 12 }}>{geoMsg}</span>}
          </div>
        </div>

        <div style={{ borderTop: '1px solid #f0f0f0', marginTop: 16, paddingTop: 16 }}>
          <div style={styles.subField}>
            <button style={styles.runBtn} onClick={handleSincronizarHierarquia} disabled={runningHier}>
              {runningHier ? 'Sincronizando...' : '▶ Sincronizar hierarquia'}
            </button>
            <span style={styles.hint}> Puxa pricing/gerente/coordenador/supervisor de cada vendedor (TGFVEN) — necessário para os gestores enxergarem os vendedores deles.</span>
            {hierMsg && <span style={{ ...styles.hint, color: '#16a34a', marginLeft: 12 }}>{hierMsg}</span>}
          </div>
          <div style={{ borderTop: '1px solid #f0f0f0', marginTop: 16, paddingTop: 16 }}>
            <Toggle
              label="Sincronizar Clientes automaticamente (Sankhya)"
              desc="Roda o Sincronizar todo dia: espelha a base com a consulta oficial (importa/religa quem entrou, desativa quem saiu, recalcula o escopo dos gestores). É o caminho definitivo — substitui as importações de carteira/equipes automáticas."
              value={settings.sincronizarAutoEnabled}
              onChange={v => set('sincronizarAutoEnabled', v)}
            />
            {settings.sincronizarAutoEnabled && (
              <>
                <div style={styles.subField}>
                  <label style={styles.label}>Horário</label>
                  <select style={styles.numberInput} value={settings.sincronizarHour} onChange={e => set('sincronizarHour', Number(e.target.value))}>
                    {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>)}
                  </select>
                  <span style={styles.hint}>Roda de madrugada para não pesar no horário de uso.</span>
                </div>
                <div style={styles.subField}>
                  <label style={styles.label}>Apagar inativos após (dias)</label>
                  <input
                    type="number" min={0} max={365}
                    style={styles.numberInput}
                    value={settings.sincDeleteGraceDays}
                    onChange={e => set('sincDeleteGraceDays', Math.max(0, Math.min(365, Number(e.target.value) || 0)))}
                  />
                  <span style={styles.hint}>Cliente que sai do Sankhya é desativado na hora (reversível) e APAGADO de vez após ficar este nº de dias seguidos fora. 0 = nunca apaga (só desativa). O histórico de check-ins é sempre preservado.</span>
                </div>
                <div style={{ ...styles.subField, marginTop: 4 }}>
                  <Toggle
                    label="Buscar alterações de hora em hora (incremental)"
                    desc="Nas outras 23 horas do dia, traz só os clientes ALTERADOS no Sankhya desde a última rodada (pela data de alteração do cadastro) — leva segundos em vez de minutos. Ela NÃO desativa, NÃO apaga e NÃO cria cliente novo: isso continua sendo trabalho da rodada completa, no horário acima. Uma alteração feita no ERP passa a aparecer no app em até 1 h."
                    value={settings.sincIncrementalEnabled}
                    onChange={v => set('sincIncrementalEnabled', v)}
                  />
                </div>
              </>
            )}
            <div style={styles.subField}>
              <button style={styles.runBtn} onClick={handleImportarDaQuery} disabled={runningQuery}>
                {runningQuery ? `Sincronizando Clientes...${pctDe(progSync) != null ? ` ${pctDe(progSync)}%` : ''}` : '🔄 Sincronizar Clientes'}
              </button>
              <BarraProgresso p={progSync} />
              <span style={styles.hint}> Espelha a base com a consulta oficial do Sankhya em tempo real: importa/religa quem entrou, desativa quem saiu, e recalcula o escopo dos gestores (quais clientes cada gerente vê).</span>
              {queryMsg && <span style={{ ...styles.hint, color: '#7c3aed', marginLeft: 12 }}>{queryMsg}</span>}
            </div>
            <div style={styles.subField}>
              <input
                type="text"
                inputMode="numeric"
                placeholder="CODVEND do vendedor (ex.: 1001)"
                value={importCodvend}
                onChange={(e) => setImportCodvend(e.target.value)}
                disabled={runningImport}
                style={{ ...styles.numberInput, width: 240, marginRight: 8 }}
              />
              <label style={{ ...styles.runBtn, display: 'inline-block', cursor: importCodvend.trim() && !runningImport ? 'pointer' : 'default', opacity: importCodvend.trim() && !runningImport ? 1 : 0.5 }}>
                {runningImport ? 'Importando...' : '⬆️ Importar clientes (app-only)'}
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  style={{ display: 'none' }}
                  disabled={!importCodvend.trim() || runningImport}
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImportarAppOnly(f); e.target.value = ''; }}
                />
              </label>
              <span style={styles.hint}> Sobe uma planilha de CNPJs que NÃO estão no Sankhya e cria os clientes SÓ no app, vinculados ao CODVEND informado, marcados "Não cadastrado no Sankhya" (não sobem pro ERP nem são apagados pelo Sincronizar). Colunas: CNPJ, Razao Social, Nome Fantasia, Logradouro, Numero, Complemento, Bairro, Municipio, UF, CEP, Telefones, E-mail. Dedup por CNPJ.</span>
              {importMsg && <span style={{ ...styles.hint, color: '#7c3aed', marginLeft: 12 }}>{importMsg}</span>}
            </div>
            <div style={styles.subField}>
              <label style={{ ...styles.runBtn, display: 'inline-block', cursor: !runningEstrelas ? 'pointer' : 'default', opacity: !runningEstrelas ? 1 : 0.5 }}>
                {runningEstrelas ? 'Importando...' : '⭐ Importar estrelas/potencial'}
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  style={{ display: 'none' }}
                  disabled={runningEstrelas}
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImportarEstrelas(f); e.target.value = ''; }}
                />
              </label>
              <span style={styles.hint}> Sobe a planilha com a aba "Potencial_Estrelas" (classificação feita pelo vendedor). Casa por CODPARC e grava estrelas (1–3), Potencial de Venda (R$) e Potencial de Compra APENAS nas células preenchidas — não apaga a classificação feita à mão. As estrelas definem a cadência da roteirização (3★ = 30 dias, 2★ = 60, 1★ = 90).</span>
              {estrelasMsg && <span style={{ ...styles.hint, color: estrelasErro ? '#dc2626' : '#7c3aed', marginLeft: 12 }}>{estrelasMsg}</span>}
            </div>
            <div style={styles.subField}>
              <label style={{ ...styles.runBtn, display: 'inline-block', cursor: !runningVisitas ? 'pointer' : 'default', opacity: !runningVisitas ? 1 : 0.5 }}>
                {runningVisitas ? 'Importando...' : '📅 Importar visitas históricas'}
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  style={{ display: 'none' }}
                  disabled={runningVisitas}
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImportarVisitas(f); e.target.value = ''; }}
                />
              </label>
              <span style={styles.hint}> Sobe a planilha com a aba "Visitas" (histórico anterior ao app). Casa por CODPARC e grava a data da ÚLTIMA visita (a mais recente por cliente) SÓ se for mais nova que a já registrada — nunca apaga um check-in real. Semeia a cadência da roteirização para clientes já visitados antes do app. Rode o "Sincronizar Clientes" ANTES, para a base já ter os CODPARC.</span>
              {visitasMsg && <span style={{ ...styles.hint, color: visitasErro ? '#dc2626' : '#7c3aed', marginLeft: 12 }}>{visitasMsg}</span>}
            </div>
            <div style={styles.subField}>
              <button style={styles.runBtn} onClick={handleEstrelaPadrao} disabled={runningEstrelaPadrao}>
                {runningEstrelaPadrao ? 'Aplicando...' : '⭐ Definir 1★ padrão (clientes sem estrela)'}
              </button>
              <span style={styles.hint}> Preenche 1★ em TODOS os clientes que estão com 0/sem estrela (não altera quem já tem 1/2/3★). Ativa a base na cadência da roteirização (1★ = 90 dias); clientes novos também passam a nascer com 1★. Escrita grande (~15 mil) — pede confirmação.</span>
              {estrelaPadraoMsg && <span style={{ ...styles.hint, color: estrelaPadraoErro ? '#dc2626' : '#7c3aed', marginLeft: 12 }}>{estrelaPadraoMsg}</span>}
            </div>
          </div>
        </div>
      </div>

      <button style={styles.saveBtn} onClick={handleSave} disabled={saving}>
        {saving ? 'Salvando...' : 'Salvar configurações'}
      </button>

      <h2 style={{ ...styles.title, fontSize: 20, marginTop: 40 }}>Motivos de Check-in</h2>
      <p style={styles.subtitle}>Opções que o vendedor seleciona ao fazer um check-in (ex: Precificação, Abastecimento...).</p>

      <div style={styles.card}>
        <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
          <input
            style={styles.reasonInput}
            placeholder="Novo motivo (ex: Precificação)"
            value={newReason}
            onChange={e => setNewReason(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addReason()}
          />
          <button style={styles.addReasonBtn} onClick={addReason}>+ Adicionar</button>
        </div>

        {reasons.length === 0 && <p style={{ color: '#999', fontSize: 14 }}>Nenhum motivo cadastrado ainda.</p>}

        {reasons.map(r => (
          <div key={r.id} style={styles.reasonRow}>
            {editingReason?.id === r.id ? (
              <>
                <input
                  style={{ ...styles.reasonInput, flex: 1 }}
                  value={editingReason.name}
                  onChange={e => setEditingReason({ ...editingReason, name: e.target.value })}
                  onKeyDown={e => e.key === 'Enter' && saveReasonEdit()}
                  autoFocus
                />
                <button style={styles.reasonBtn} onClick={saveReasonEdit}>Salvar</button>
                <button style={styles.reasonBtn} onClick={() => setEditingReason(null)}>Cancelar</button>
              </>
            ) : (
              <>
                <span style={{ flex: 1, fontSize: 14, color: r.active ? '#1a1a2e' : '#aaa', textDecoration: r.active ? 'none' : 'line-through' }}>
                  {r.name}
                </span>
                <button style={styles.reasonBtn} onClick={() => setEditingReason(r)}>Editar</button>
                <button style={styles.reasonBtn} onClick={() => toggleReason(r)}>{r.active ? 'Desativar' : 'Ativar'}</button>
                <button style={{ ...styles.reasonBtn, color: '#dc2626', borderColor: '#fecaca' }} onClick={() => deleteReason(r)}>Excluir</button>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function Toggle({ label, desc, value, onChange }: { label: string; desc: string; value: boolean; onChange: (v: boolean) => void }) {
  return (
    <div style={styles.toggleRow}>
      <div style={{ flex: 1 }}>
        <div style={styles.toggleLabel}>{label}</div>
        <div style={styles.toggleDesc}>{desc}</div>
      </div>
      <button
        type="button"
        onClick={() => onChange(!value)}
        style={{ ...styles.switch, background: value ? '#f5821f' : '#d1d5db' }}
      >
        <span style={{ ...styles.switchKnob, transform: value ? 'translateX(20px)' : 'translateX(0)' }} />
      </button>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  title: { fontSize: 24, fontWeight: 700, color: '#1a1a2e', margin: 0 },
  subtitle: { color: '#666', margin: '4px 0 24px', fontSize: 14 },
  saved: { background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0', borderRadius: 10, padding: '12px 16px', marginBottom: 16, fontSize: 14 },
  card: { background: '#fff', borderRadius: 12, padding: 20, marginBottom: 16, boxShadow: '0 1px 8px rgba(0,0,0,0.06)' },
  toggleRow: { display: 'flex', alignItems: 'center', gap: 16 },
  toggleLabel: { fontWeight: 600, fontSize: 15, color: '#1a1a2e' },
  toggleDesc: { fontSize: 13, color: '#666', marginTop: 2 },
  switch: { width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer', position: 'relative', padding: 0, transition: 'background 0.2s', flexShrink: 0 },
  switchKnob: { display: 'block', width: 20, height: 20, borderRadius: 10, background: '#fff', position: 'absolute', top: 2, left: 2, transition: 'transform 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' },
  subField: { marginTop: 16, paddingTop: 16, borderTop: '1px solid #f0f0f0' },
  label: { display: 'block', fontSize: 13, fontWeight: 600, color: '#444', marginBottom: 6 },
  numberInput: { width: 140, border: '1px solid #e0e0e0', borderRadius: 8, padding: '9px 12px', fontSize: 14 },
  hint: { display: 'block', fontSize: 12, color: '#888', marginTop: 6 },
  saveBtn: { background: '#f5821f', color: '#fff', border: 'none', borderRadius: 10, padding: '12px 28px', fontWeight: 700, cursor: 'pointer', fontSize: 15 },
  runBtn: { background: '#fff', color: '#c2410c', border: '1px solid #fed7aa', borderRadius: 8, padding: '8px 16px', fontWeight: 600, cursor: 'pointer', fontSize: 14 },
  reasonInput: { flex: 1, border: '1px solid #e0e0e0', borderRadius: 8, padding: '9px 12px', fontSize: 14 },
  addReasonBtn: { background: '#f5821f', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 18px', fontWeight: 600, cursor: 'pointer', fontSize: 14, whiteSpace: 'nowrap' },
  reasonRow: { display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderBottom: '1px solid #f5f5f5' },
  reasonBtn: { background: 'none', border: '1px solid #e0e0e0', borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer', color: '#555' },
};
